// Composants de base du design system.
//
// Trois règles portées ici pour ne pas être réinventées dans chaque écran :
// pastille (999px) pour les boutons, 1px de filet plutôt qu'une ombre, et
// métadonnée en mono MAJUSCULES.
import type { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useCallback, useId, useRef, useState } from 'react';
import { appendDictated, useDictation } from '../voice/useDictation';
import { Icon, type IconName } from './icons';
import './ui.css';

// Variantes du design system Zygosight (`zygosight-ds/DESIGN-zygosight.md`,
// « 1. Button »). Les anciens noms restent : primary = principal (encre),
// ghost = discret (filet), accent = cyan.
type ButtonVariant = 'primary' | 'ghost' | 'accent' | 'link' | 'danger' | 'link-danger';

const BUTTON_CLASS: Record<ButtonVariant, string> = {
  primary: 'zy-btn--principal',
  ghost: 'zy-btn--discret',
  accent: 'zy-btn--accent',
  link: 'zy-btn--lien',
  danger: 'zy-btn--danger',
  'link-danger': 'zy-btn--lien zy-btn--danger',
};

/** Les classes d'un bouton, pour un lien (`<a>`, `Link`) qui doit en avoir l'air. */
export function buttonClass(variant: ButtonVariant = 'primary', size: 's' | 'm' = 'm') {
  return `zy-btn ${BUTTON_CLASS[variant]}${size === 's' ? ' zy-btn--s' : ''}`;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** M (40 px) par défaut ; S (32 px) dans les tableaux, les lignes et les barres d'outils. */
  size?: 's' | 'm';
  /** Pictogramme à gauche du libellé. */
  icon?: IconName;
  loading?: boolean;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'm',
  icon,
  loading = false,
  disabled,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      className={`${buttonClass(variant, size)} ${className}`.trim()}
      // Un bouton en cours d'envoi doit être inerte : sans cela, un double clic
      // crée deux organisations ou deux insights.
      disabled={disabled || loading}
      // `aria-busy` annonce l'attente aux lecteurs d'écran ; la feuille du
      // design system garde la largeur du bouton et y dessine l'indicateur.
      aria-busy={loading || undefined}
      {...rest}
    >
      {icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Obligatoire : lu par les lecteurs d'écran ET affiché en info-bulle. */
  label: string;
  icon: IconName;
  /** Rougit au survol : suppression, retrait. */
  danger?: boolean;
  size?: 's' | 'm';
}

/** Bouton rond qui ne contient qu'un pictogramme (« 2. IconButton »). Pour une
 *  action importante, préférer un `Button` avec pictogramme : une icône seule
 *  ne dit rien d'elle-même. */
export function IconButton({ label, icon, danger = false, size = 's', className = '', type = 'button', ...rest }: IconButtonProps) {
  return (
    <button
      type={type}
      className={`zy-icon-btn${size === 'm' ? ' zy-icon-btn--m' : ''}${danger ? ' zy-icon-btn--danger' : ''} ${className}`.trim()}
      aria-label={label}
      data-tip={label}
      {...rest}
    >
      <Icon name={icon} />
    </button>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner" role="status" aria-live="polite">
      <span className="sr-only">{label ?? 'Chargement…'}</span>
    </span>
  );
}

/** Icône micro. Deux états de tracé : au repos, et pendant l'écoute (le corps
 *  du micro se remplit — pas d'agrandissement, la charte proscrit le scale). */
export function MicIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <rect x="9" y="2" width="6" height="11" rx="3" fill={active ? 'currentColor' : 'none'} />
      <path d="M5 10a7 7 0 0 0 14 0M12 17v4" />
    </svg>
  );
}

/** Bouton de dictée. Ne rend RIEN si le navigateur n'a pas l'API : un bouton
 *  qui ne peut pas fonctionner est pire qu'un bouton absent. */
export function MicButton({ dictation }: { dictation: ReturnType<typeof useDictation> }) {
  if (!dictation.supported) return null;
  const label = dictation.listening ? 'Arrêter la dictée' : 'Dicter au micro';
  return (
    <button
      type="button"
      className={`zy-icon-btn${dictation.listening ? ' mic-btn-on' : ''}`}
      onClick={dictation.toggle}
      // Info-bulle pour la souris, `aria-label` pour les lecteurs d'écran : le
      // bouton n'a pas de texte visible.
      data-tip={label}
      aria-label={label}
      aria-pressed={dictation.listening}
    >
      <MicIcon active={dictation.listening} />
    </button>
  );
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  /** Force l'affichage du micro, ou le retire. Par défaut il n'apparaît que
   *  sur les champs de texte libre (voir `TYPES_SANS_DICTEE`). */
  voice?: boolean;
}

// Types de champ où dicter n'a aucun sens, ou serait franchement malvenu :
// on ne prononce pas un mot de passe à voix haute, et une adresse e-mail
// dictée arrive épelée (« arobase », « point ») — plus lente à corriger qu'à
// taper.
const TYPES_SANS_DICTEE = ['password', 'email', 'number', 'tel', 'url', 'date', 'time', 'checkbox', 'radio', 'file'];

export function Field({ label, hint, error, id, voice, ...rest }: FieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const type = String(rest.type ?? 'text');

  // L'erreur de dictée s'affiche à la place de l'indication, sans jamais
  // remplacer l'erreur de validation du champ — celle-ci prime toujours.
  const described = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="zy-field">
      <label className="zy-field__label" htmlFor={fieldId}>
        {label}
      </label>
      <VoiceInput
        id={fieldId}
        voice={voice ?? !TYPES_SANS_DICTEE.includes(type)}
        // L'erreur se lit sur `aria-invalid` : la feuille du design system
        // met alors la bordure en rouge.
        aria-invalid={error ? true : undefined}
        aria-describedby={described || undefined}
        {...rest}
      />
      {hint && !error ? (
        <span className="zy-field__aide" id={hintId}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="zy-field__erreur" id={errorId}>
          <Icon name="alert" className="zy-icon" />
          {error}
        </span>
      ) : null}
    </div>
  );
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** `ligne` : dans une ligne de tableau ou une barre d'outils, sans bordure
   *  tant qu'on ne le survole pas. */
  variant?: 'ligne';
  children: ReactNode;
}

/** Liste déroulante (« 4. Select ») : un `<select>` natif, habillé. Il reste
 *  accessible et garde le sélecteur natif sur mobile. `className` habille le
 *  cadre (largeur, place dans la ligne), pas la liste elle-même. Moins de
 *  5 choix exclusifs : des boutons radio valent mieux. */
export function Select({ variant, className = '', children, ...rest }: SelectProps) {
  return (
    <span className={`zy-select${variant === 'ligne' ? ' zy-select--ligne' : ''} ${className}`.trim()}>
      <select className="zy-input" {...rest}>
        {children}
      </select>
    </span>
  );
}

interface VoiceInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** `false` retire le micro : le champ redevient un simple `<input>` encadré. */
  voice?: boolean;
}

/** Un `<input>` nu, avec le micro de dictée posé dans son cadre. C'est le cœur
 *  de `Field`, sans libellé ni indication — pour les listes éditables, où chaque
 *  ligne porte son libellé en `aria-label`. */
export function VoiceInput({ voice = true, ...rest }: VoiceInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Le champ est CONTRÔLÉ par l'appelant (`value` + `onChange`). Pour y injecter
  // du texte sans imposer une nouvelle prop à la trentaine d'appels existants,
  // on écrit par le setter natif puis on émet un `input` : React le voit alors
  // exactement comme une frappe clavier et rappelle le `onChange` de l'appelant.
  // Affecter `input.value` directement ne suffirait pas — React ne s'en
  // apercevrait pas, et la valeur serait écrasée au rendu suivant.
  const insert = useCallback((text: string) => {
    const input = inputRef.current;
    if (!input) return;
    const setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
    setter?.call(input, appendDictated(input.value, text));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  const dictation = useDictation(insert);

  return (
    <>
      <div className={voice && dictation.supported ? 'zy-input-groupe' : undefined}>
        <input ref={inputRef} className="zy-input" {...rest} />
        {voice ? <MicButton dictation={dictation} /> : null}
      </div>
      {dictation.error ? <span className="zy-field__erreur">{dictation.error}</span> : null}
    </>
  );
}

// --- Pastille (« 6. Chip ») ---------------------------------------------------

/** Les 8 teintes de catégorie, sous leurs noms de l'application (`taxonomy/colors`). */
type ChipHue = 'cyan' | 'orange' | 'red' | 'amber' | 'green' | 'violet' | 'pink' | 'blue';
const CHIP_CAT: Record<ChipHue, string> = {
  cyan: 'cyan',
  orange: 'orange',
  red: 'rouge',
  amber: 'ambre',
  green: 'vert',
  violet: 'violet',
  pink: 'rose',
  blue: 'bleu',
};

export type ChipState = 'succes' | 'avertissement' | 'erreur' | 'vide';
// Un état ne se dit jamais par la couleur seule : chaque état a son pictogramme.
const CHIP_STATE_ICON: Record<ChipState, IconName | null> = {
  succes: 'check',
  avertissement: 'alert',
  erreur: 'close',
  vide: null,
};

/** Les anciens tons (`tone-good`…) encore portés par les tables de libellés,
 *  traduits en états de pastille. Un ton inconnu ou vide : pas d'état. */
export function chipState(tone: string | null | undefined): ChipState | undefined {
  switch (tone) {
    case 'tone-good':
      return 'succes';
    case 'tone-warn':
      return 'avertissement';
    case 'tone-bad':
      return 'erreur';
    case 'tone-empty':
      return 'vide';
    default:
      return undefined;
  }
}

interface ChipOptions {
  /** Catégorie (type d'étude, tag, sévérité…) : une teinte, jamais un état. */
  hue?: ChipHue | null;
  /** État : succès, avertissement, erreur, vide. */
  state?: ChipState | null;
  /** Contenu produit ou proposé par l'IA. */
  ia?: boolean;
}

/** Les classes d'une pastille, pour un lien (`Link`) qui doit en avoir l'air. */
export function chipClass({ state, ia }: ChipOptions = {}) {
  return `zy-chip${state ? ` zy-chip--${state}` : ''}${ia ? ' zy-chip--ia' : ''}`;
}

interface ChipProps extends ChipOptions, HTMLAttributes<HTMLSpanElement> {
  /** Pictogramme à gauche ; par défaut celui de l'état, ou l'étincelle pour l'IA. */
  icon?: IconName | null;
  children: ReactNode;
}

/** Pastille de 24 px : une catégorie (`hue`), un état (`state`) ou une marque IA. */
export function Chip({ hue, state, ia = false, icon, className = '', children, ...rest }: ChipProps) {
  const shown = icon !== undefined ? icon : state ? CHIP_STATE_ICON[state] : ia ? 'sparkles' : null;
  return (
    <span className={`${chipClass({ state, ia })} ${className}`.trim()} data-cat={hue ? CHIP_CAT[hue] : undefined} {...rest}>
      {shown ? <Icon name={shown} className="zy-icon" /> : null}
      {children}
    </span>
  );
}

type NoteTone = 'info' | 'avertissement' | 'erreur' | 'succes' | 'lecture';

const NOTE_ICON: Record<NoteTone, IconName> = {
  info: 'info',
  avertissement: 'alert',
  erreur: 'alert',
  succes: 'check',
  lecture: 'lock',
};

interface NoteProps extends HTMLAttributes<HTMLDivElement> {
  /** info (par défaut) : expliquer · avertissement : à compléter, à confirmer ·
   *  erreur : bloquant, avec comment corriger · succes · lecture : lecture seule. */
  tone?: NoteTone;
  children: ReactNode;
}

/** Note encadrée (« 7. Note ») : un pictogramme, puis le texte, avec une action
 *  facultative en lien. Une seule par zone ; une simple phrase d'aide reste un
 *  texte (`zy-texte-muet`), pas une note. */
export function Note({ tone = 'info', children, className = '', ...rest }: NoteProps) {
  return (
    <div className={`zy-note${tone === 'info' ? '' : ` zy-note--${tone}`} ${className}`.trim()} {...rest}>
      <Icon name={NOTE_ICON[tone]} className="zy-icon" />
      <div>{children}</div>
    </div>
  );
}

/** Bandeau d'erreur. `role="alert"` pour que l'échec soit annoncé, pas seulement affiché. */
export function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <Note tone="erreur" role="alert">
      {children}
    </Note>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <span className="zy-eyebrow">{children}</span>;
}

interface EmptyStateProps {
  title: string;
  description: string;
  action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="zy-empty">
      <h2 className="t-h3">{title}</h2>
      <p>{description}</p>
      {action ? <div className="zy-empty__actions">{action}</div> : null}
    </div>
  );
}

/** Chiffre clé (« 14. Stat ») : sur-titre, valeur, précision facultative. */
export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string | undefined }) {
  return (
    <div className="zy-stat">
      <span className="zy-eyebrow">{label}</span>
      <span className="zy-stat__valeur">{value}</span>
      {hint ? <span className="zy-texte-muet">{hint}</span> : null}
    </div>
  );
}

const AVATAR_CATS = ['cyan', 'orange', 'rouge', 'ambre', 'vert', 'violet', 'rose', 'bleu'] as const;

/** La teinte d'une personne, toujours la même : calculée depuis son identifiant. */
export function avatarCat(seed: string): (typeof AVATAR_CATS)[number] {
  let h = 0;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_CATS[h % AVATAR_CATS.length]!;
}

/** Avatar (« 15. Avatar ») : les initiales, une teinte tirée de la donnée. Décoratif. */
export function Avatar({
  initials,
  seed,
  size = 's',
  title,
}: {
  initials: string;
  seed: string;
  size?: 's' | 'm';
  title?: string | undefined;
}) {
  return (
    <span className={`zy-avatar${size === 'm' ? ' zy-avatar--m' : ''}`} data-cat={avatarCat(seed)} title={title} aria-hidden="true">
      {initials}
    </span>
  );
}

/** Copie un texte dans le presse-papiers, et le dit. */
export function CopyButton({ text, label = 'Copier' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => setCopied(true), () => setCopied(false));
      }}
    >
      {copied ? 'Copié' : label}
    </Button>
  );
}
